import { FilterDef } from "@/stores/filters/FilterDef";
import { FilterParamValue } from "@/stores/filters/FilterParamValue.ts";
import { Interval } from "luxon";

export enum GOAL_SCOPES {
	PLANNED = 'planned',
	IN_COLLECTION = 'collected',
	UNPLANNED = 'unplanned',
	EVALUATED = 'evaluated',
}

export enum LESSON_TYPES {
	THEMES = 'themes',
	ACTIVITIES = 'activities',
	COLLECTIVE = 'collective',
}

export class GoalFilterDef extends FilterDef {
	constructor() {
		super([
			'mainGroups',
			'keywords',
			'tags',

			// Required for evaluations
			'collectionIds',
			'usage',
			'start',
			'end',
			'classroomIds',
			'lessonTypes',
			'pupilIds',
			'evaluationsStart',
			'evaluationsEnd'
		]);
	}

	/**
	 * As goal filtering is mostly done client-side (with all the goals loaded in the store already),
	 * there is no use of executing a server request if only certain parameters changed.
	 *
	 * This method determines if a server request is needed based on the previously set filter.
	 * @param newFilter
	 */
	requiresFreshServerData(newFilter: GoalFilterDef): boolean {

		const serverSideFilteredParams = [
			'collectionIds',
			'usage',
			'start',
			'end',
			'classroomIds',
			'lessonTypes',
			'pupilIds',
			'evaluationsStart',
			'evaluationsEnd'
		];

		return serverSideFilteredParams.some(param => {

			const thisValues = this.getParamValues(param);
			const otherValues = newFilter.getParamValues(param);

			if (thisValues.length !== otherValues.length) {
				return true;
			}

			return thisValues.some((value, index) => value.id !== otherValues[index].id);
		});


	}

	set collection(collectionId: string) {
		if (!collectionId) {
			this.clearParam('collectionIds');
			return;
		}
		this.setParamValues('collectionIds', [new FilterParamValue(collectionId, collectionId)]);
	}

	get collection(): string | null {
		return this.getParamValues('collectionIds')[0]?.id.toString() || null;
	}

	setInterval(interval: Interval | null) {
		if (interval && interval.start && interval.end) {
			this.setParamValues('start', [new FilterParamValue(interval.start.toUTC().toISO(), interval.start.toUTC().toISO())]);
			this.setParamValues('end', [new FilterParamValue(interval.end.toUTC().toISO(), interval.end.toUTC().toISO())]);
		} else {
			this.clearParam('start');
			this.clearParam('end');
		}
	}

	get goalScopes(): GOAL_SCOPES[] | null {
		const values = this.getParamValues('usage');
		if (!values || values.length === 0) {
			return null;
		}

		return values.map(v => v.id.toString()) as GOAL_SCOPES[];
	}

	set goalScopes(values: GOAL_SCOPES[]) {
		this.setParamValues('usage', values.map(id => new FilterParamValue(id, id)));
	}

	set classroomIds(value: string[]) {
		this.setParamValues('classroomIds', value.map(id => new FilterParamValue(id, id)));
	}

	get classroomIds(): string[] {
		return this.getParamValues('classroomIds').map(v => v.id.toString());
	}

	set start(value: Date) {
		this.setParamValues('start', [new FilterParamValue(value.toISOString(), value.toISOString())]);
	}

	get start(): Date | null {
		// cast to Date
		if (!this.getParamValues('start')[0]?.id) {
			return null;
		}

		return new Date(this.getParamValues('start')[0]?.id.toString());
	}

	set end(value: Date) {
		this.setParamValues('end', [new FilterParamValue(value.toISOString(), value.toISOString())]);
	}

	get end(): Date | null {
		// cast to Date
		if (!this.getParamValues('end')[0]?.id) {
			return null;
		}

		return new Date(this.getParamValues('end')[0]?.id.toString());
	}

	/**
	 * Bound the evaluations loaded with the goals separately from `start`/`end`, which select
	 * the goals themselves (e.g. the goals planned in an agenda week). A null bound is sent as
	 * an empty value, which tells the API the evaluations are not bounded on that side.
	 * Without a range, the API bounds the evaluations by `start`/`end`.
	 * @param start
	 * @param end
	 */
	setEvaluationsRange(start: Date | null, end: Date | null) {
		this.setParamValues('evaluationsStart', [this.evaluationsBound(start)]);
		this.setParamValues('evaluationsEnd', [this.evaluationsBound(end)]);
	}

	/**
	 * Let the API bound the evaluations by `start`/`end` again.
	 */
	clearEvaluationsRange() {
		this.clearParam('evaluationsStart');
		this.clearParam('evaluationsEnd');
	}

	private evaluationsBound(value: Date | null): FilterParamValue {
		const id = value ? value.toISOString() : '';
		return new FilterParamValue(id, id);
	}

	set lessonTypes(value: LESSON_TYPES[]) {
		this.setParamValues('lessonTypes', value);
	}

	get lessonTypes(): LESSON_TYPES[] {
		return this.getParamValues('lessonTypes').map(v => v.id.toString()) as LESSON_TYPES[];
	}

	set pupilIds(value: string[]) {
		this.setParamValues('pupilIds', value.map(id => new FilterParamValue(id, id)));
	}

	get pupilIds(): string[] {
		return this.getParamValues('pupilIds').map(v => v.id.toString());
	}
}
